import apiClient from './api';
import axios from 'axios';

// Create axios instance for clinical supervisor endpoints
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 300000
});

// Dashboard
export const getDashboard = async (token) => {
  const response = await apiClient.get('/clinical-supervisor/dashboard', token);
  return response.data;
};

// Therapist Management
export const getAssignedTherapists = async (includeStats, token) => {
  // Need to use axios directly for query params
  const config = token ? { headers: { 'x-auth-token': token }, params: { includeStats } } : { params: { includeStats } };
  const response = await axiosInstance.get('/clinical-supervisor/therapists', config);
  return response.data;
};

export const getTherapistDetails = async (therapistId, token) => {
  const response = await apiClient.get(`/clinical-supervisor/therapists/${therapistId}`, token);
  return response.data;
};

export const getTherapistClients = async (therapistId, token) => {
  const response = await apiClient.get(`/clinical-supervisor/therapists/${therapistId}/clients`, token);
  return response.data;
};

export const getTherapistConsultations = async (therapistId, status, limit, offset, token) => {
  // Need to use axios directly for query params
  const config = token ? { headers: { 'x-auth-token': token }, params: { status, limit, offset } } : { params: { status, limit, offset } };
  const response = await axiosInstance.get(`/clinical-supervisor/therapists/${therapistId}/consultations`, config);
  return response.data;
};

export const assignTherapist = async (therapistId, token) => {
  const response = await apiClient.post('/clinical-supervisor/therapists/assign', { therapistId }, token);
  return response.data;
};

export const unassignTherapist = async (therapistId, token) => {
  const response = await apiClient.delete(`/clinical-supervisor/therapists/${therapistId}/unassign`, token);
  return response.data;
};

// Consultation Access
export const getConsultation = async (consultationId, token) => {
  const response = await apiClient.get(`/clinical-supervisor/consultations/${consultationId}`, token);
  return response.data;
};

// Client Access (Read-only for MVP)
export const getClientDetails = async (clientId, token) => {
  const response = await apiClient.get(`/clinical-supervisor/client/${clientId}`, token);
  return response.data;
};

export const getClientConversations = async (clientId, token) => {
  const response = await apiClient.get(`/clinical-supervisor/client/${clientId}/conversations`, token);
  return response.data;
};

export const getClientGoals = async (clientId, token) => {
  const response = await apiClient.get(`/clinical-supervisor/client/${clientId}/goals`, token);
  return response.data;
};

export const getClientJournal = async (clientId, token) => {
  const response = await apiClient.get(`/clinical-supervisor/client/${clientId}/journal`, token);
  return response.data;
};

export const getClientProgress = async (clientId, token) => {
  const response = await apiClient.get(`/clinical-supervisor/client/${clientId}/progress`, token);
  return response.data;
};

export const getClientCheckIns = async (clientId, token) => {
  const response = await apiClient.get(`/clinical-supervisor/client/${clientId}/checkins`, token);
  return response.data;
};

export const getClientActivityLog = async (clientId, page = 1, limit = 20, filters = {}, token) => {
  // Need to use axios directly for query params
  const config = token
    ? { headers: { 'x-auth-token': token }, params: { page, limit, ...filters } }
    : { params: { page, limit, ...filters } };
  const response = await axiosInstance.get(`/clinical-supervisor/client/${clientId}/activity`, config);
  return response.data;
};

// Care Note Review
export const getPendingCareNotes = async (token) => {
  const response = await apiClient.get('/clinical-supervisor/care-notes/pending', token);
  return response.data;
};

export const reviewCareNote = async (consultationId, careNoteId, approved, feedback, editedContent, token) => {
  const response = await apiClient.put(
    `/clinical-supervisor/care-notes/${consultationId}/${careNoteId}/review`,
    { approved, feedback, editedContent },
    token
  );
  return response.data;
};

// Reports
export const getReports = async (period, token) => {
  // Need to use axios directly for query params
  const config = token ? { headers: { 'x-auth-token': token }, params: { period } } : { params: { period } };
  const response = await axiosInstance.get('/clinical-supervisor/reports', config);
  return response.data;
};

// Comprehensive Activity Log for all supervised therapists and clients
export const getSupervisorActivityLog = async (token, params = {}) => {
  // Need to use axios directly for query params
  const config = token ? { headers: { 'x-auth-token': token }, params } : { params };
  const response = await axiosInstance.get('/clinical-supervisor/activity', config);
  return response.data;
};

// ==================== CLIENT MANAGEMENT ====================

// Get all clients in a specific service group
export const getClientsInServiceGroup = async (groupId, params = {}, token) => {
  const config = token ? { headers: { 'x-auth-token': token }, params } : { params };
  const response = await axiosInstance.get(`/clinical-supervisor/service-groups/${groupId}/clients`, config);
  return response.data;
};

// Get all unassigned clients across supervisor's service groups
export const getUnassignedClients = async (params = {}, token) => {
  const config = token ? { headers: { 'x-auth-token': token }, params } : { params };
  const response = await axiosInstance.get('/clinical-supervisor/clients/unassigned', config);
  return response.data;
};

// Assign clients to a therapist
export const assignClientsToTherapist = async (clientIds, therapistId, token) => {
  const response = await apiClient.post(
    '/clinical-supervisor/clients/assign',
    { clientIds, therapistId },
    token
  );
  return response.data;
};

// Transfer clients between therapists
export const transferClients = async (clientIds, fromTherapistId, toTherapistId, token) => {
  const response = await apiClient.post(
    '/clinical-supervisor/clients/transfer',
    { clientIds, fromTherapistId, toTherapistId },
    token
  );
  return response.data;
};

// Change client's service group
export const changeClientServiceGroup = async (clientIds, targetServiceGroupId, token) => {
  const response = await apiClient.post(
    '/clinical-supervisor/clients/change-service-group',
    { clientIds, targetServiceGroupId },
    token
  );
  return response.data;
};

// Unassign clients from their therapist
export const unassignClients = async (clientIds, token) => {
  const response = await apiClient.post(
    '/clinical-supervisor/clients/unassign',
    { clientIds },
    token
  );
  return response.data;
};

// Alert Management
export const getPendingAlerts = async (token) => {
  const response = await apiClient.get('/clinical-supervisor/alerts/pending', token);
  return response.data;
};

export const getClientAlerts = async (clientId, token) => {
  const response = await apiClient.get(`/clinical-supervisor/client/${clientId}/alerts`, token);
  return response.data;
};
